import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { Modal, Button, Input, Select } from './UI';
import { ReviewCheckbox } from './ReviewMark';
import { TBANK_BIK, useTbankOrgs } from './bank/PaymentDrafts';
import { useData } from '../App';
import { CompanyProfile, Counterparty, CounterpartyType, PaymentMethod, Project, TaxAccount, Transaction, TransactionItem,
         TransactionKind, TransactionStatus, TransactionType, INCOME_BASES } from '../types';
import { CrmDebtHint, OrgSelect, ProjectSelect, useCrmBalances, useOrgChoice, PayKindTabs } from './FormParts';
import { api } from '../services/api';
import { incomeVatRateFor, vatModeFor } from '../utils/taxLogic';
import { useVolumeProject, VolumeLine, VolumePick, volumeProblems } from './VolumePick';
import { money } from './billing/shared';
import { accountLabel, formatDay, formatMoney, todayISO } from '../utils/format';

export type OperationMode = 'INCOME' | 'PAYMENT' | 'PURCHASE' | 'PAYOUT' | 'TAX' | 'WITHDRAWAL' | 'DIVIDENDS';

export const MODE_TITLES: Record<OperationMode, string> = {
    INCOME: 'Приход без налогов', PAYMENT: 'Оплата', PURCHASE: 'Покупка', PAYOUT: 'Выплата самозанятому / физлицу',
    TAX: 'Уплата налогов', WITHDRAWAL: 'Вывод себе (личные нужды)', DIVIDENDS: 'Дивиденды',
};

const VAT_RATES = [
    { value: '22', label: '22% (общая)' }, { value: '20', label: '20% (до 2026)' }, { value: '10', label: '10%' },
    { value: '7', label: '7% (УСН)' }, { value: '5', label: '5% (УСН)' }, { value: '0', label: 'Без НДС' },
];

// Режим формы по уже сохранённой операции
export const modeOf = (t: Transaction, cp?: Counterparty): OperationMode => {
    if (t.type === TransactionType.INCOME) return 'INCOME';
    if (t.kind === 'TAX') return 'TAX';
    if (t.kind === 'OWNER_WITHDRAWAL') return 'WITHDRAWAL';
    if (t.kind === 'DIVIDENDS') return 'DIVIDENDS';
    if (!t.counterpartyId) return 'PURCHASE';
    if (cp?.type === CounterpartyType.INDIVIDUAL) return 'PAYOUT';
    return 'PAYMENT';
};

const newItem = (): TransactionItem => ({ id: `item_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, name: '', quantity: 1, unit: 'шт', price: 0, amount: 0 });

export const TransactionForm: React.FC<{
    mode: OperationMode | null;
    onClose: () => void;
    orgs: CompanyProfile[];
    fixedOrgId: string | null;  // выбранная в шапке организация; null — режим «Все организации»
    counterparties: Counterparty[];
    projects: Project[];
    editing?: Transaction | null;
    // Выплата самозанятому открывается из формы «Зарплата»: переключатель вида выплаты возвращает в неё
    onSwitchPayKind?: (kind: 'SALARY' | 'UNOFFICIAL_SALARY') => void;
}> = ({ mode, onClose, orgs, fixedOrgId, counterparties, projects, editing, onSwitchPayKind }) => {
    const { addTransaction, updateTransaction, showNotification, refreshData } = useData();
    const [orgId, setOrgId, rememberOrg] = useOrgChoice(!!mode, orgs, fixedOrgId, editing);
    const org = orgs.find(o => o.id === orgId);
    const [projectId, setProjectId] = useState('');
    const [date, setDate] = useState(todayISO());
    const [counterpartyId, setCounterpartyId] = useState('');
    const crmBalances = useCrmBalances(mode === 'PAYOUT' && !editing);
    const [category, setCategory] = useState('');
    const [kind, setKind] = useState<'REGULAR' | 'NON_TAX'>('REGULAR');
    const [items, setItems] = useState<TransactionItem[]>([newItem()]);
    const [amount, setAmount] = useState('');
    const [vatRate, setVatRate] = useState(0);
    const [method, setMethod] = useState<PaymentMethod>('bank');
    const [accountId, setAccountId] = useState('');
    const [hasDocument, setHasDocument] = useState(true);
    const [taxAccount, setTaxAccount] = useState<TaxAccount>('ENS');
    const [status, setStatus] = useState<TransactionStatus>(TransactionStatus.PENDING);
    const [divNdfl, setDivNdfl] = useState<number | null>(null);
    const [payoutLabel, setPayoutLabel] = useState<'SALARY' | 'BONUS'>('SALARY');
    // «ЗП + премия»: часть выплаты — премия (только внутренний учёт и планирование, в документы не идёт)
    const [bonusSplit, setBonusSplit] = useState(false);
    const [bonusText, setBonusText] = useState('');
    const [sendToCrm, setSendToCrm] = useState(true);
    // Подвид «Вывода себе»: '' — Бонус (статья партнёра), SALARY — ЗП партнёра (статья «ЗП»), BONUS — премия партнёра
    const [wdSubtype, setWdSubtype] = useState<'' | 'SALARY' | 'BONUS'>('');
    const [incomeBasis, setIncomeBasis] = useState('');
    // Стык годов: операция в январе за прошлый год (в декабре — за следующий) идёт в планирование того года
    const [planningYear, setPlanningYear] = useState<number | null>(null);
    const [reviewed, setReviewed] = useState(false);
    const [volumeLines, setVolumeLines] = useState<VolumeLine[] | null>(null);
    const [busy, setBusy] = useState(false);

    const isIncome = mode === 'INCOME';
    // Приход «не доход»: кошелёк растёт, в доход УСН и НДС не входит — только с основанием
    const notIncomeChosen = isIncome && kind === 'NON_TAX';
    // «Приход» — неофициальные деньги на баланс (лом, заём, взнос): без налогов, НДС и документов.
    // Официальная выручка — только через «Реализацию»; старые/импортированные официальные приходы редактируются как раньше.
    const informal = isIncome && (!editing || editing.kind === 'NON_TAX');
    const amountOnly = mode === 'TAX' || mode === 'WITHDRAWAL' || mode === 'DIVIDENDS' || informal;
    const notIncome = informal || notIncomeChosen;
    const needsBasis = notIncome && method === 'cash';
    const isSelfEmployedPayout = mode === 'PAYOUT';
    // Выплата самозанятому по объекту с прайсом подрядчика — задание с объёмами из квоты ИП, акт и передача в CRM
    // (14.10.2026: проводится здесь, как раньше; отдельной вкладки в «Объёмах работ» больше нет)
    const volumeProject = useVolumeProject(isSelfEmployedPayout && !editing ? projectId : '', orgId);
    const volumeMode = isSelfEmployedPayout && !editing && volumeProject;
    // Закрытые объекты с остатком квоты ИП (демонтажи) — тоже в списке выплаты самозанятому
    const [poolProjects, setPoolProjects] = useState<string[]>([]);
    useEffect(() => {
        setPoolProjects([]);
        if (!isSelfEmployedPayout || !orgId) return;
        let alive = true;
        api.poolProjects(orgId).then(ids => { if (alive) setPoolProjects(ids); }).catch(() => {});
        return () => { alive = false; };
    }, [isSelfEmployedPayout, orgId]);
    const year = Number(date.slice(0, 4));
    const vatGeneral = vatModeFor(org, year) === 'GENERAL';
    const isUSN6 = org?.taxSystem?.includes('УСН 6%') ?? false;

    useEffect(() => {
        if (!mode) return;
        const d = editing?.date || todayISO();
        setDate(d);
        setCounterpartyId(editing?.counterpartyId || '');
        setCategory(editing?.category || { INCOME: 'Сдача лома / обрезков кабеля', PAYMENT: 'Оплата', PURCHASE: 'Покупка', PAYOUT: 'Зарплата',
                                           TAX: 'Пополнение ЕНС', WITHDRAWAL: 'Вывод себе', DIVIDENDS: 'Дивиденды' }[mode]);
        setKind(editing?.kind === 'NON_TAX' ? 'NON_TAX' : 'REGULAR');
        setItems(editing?.items?.length ? editing.items : [{ ...newItem(), name: editing?.description || '', price: editing?.amount || 0, amount: editing?.amount || 0 }]);
        setAmount(editing ? String(editing.amount) : '');
        const rate = editing ? (editing.vatRate || 0) : 0;
        setVatRate(rate);
        setMethod(editing?.paymentMethod || (mode === 'INCOME' ? 'cash' : 'bank'));
        setAccountId(editing?.bankAccountId || org?.activeAccountId || org?.accounts[0]?.id || '');
        setHasDocument(editing ? editing.hasDocument !== false : true);
        setTaxAccount(editing?.taxAccount || 'ENS');
        setStatus(editing?.status || TransactionStatus.PENDING);
        setProjectId(editing?.projectId || '');
        setPayoutLabel(editing?.payoutLabel === 'BONUS' ? 'BONUS' : 'SALARY');
        setBonusSplit(!!editing?.bonusAmount && editing.payoutLabel === 'SALARY');
        setBonusText(editing?.bonusAmount ? String(editing.bonusAmount) : '');
        setSendToCrm(true);
        setWdSubtype(editing?.kind === 'OWNER_WITHDRAWAL' && editing.payoutLabel ? editing.payoutLabel : '');
        setIncomeBasis(editing?.incomeBasis || '');
        setPlanningYear(editing?.planningYear ?? null);
        setReviewed(false);
        setVolumeLines(null);
    }, [mode, editing, org]);
    useEffect(() => setVolumeLines(null), [projectId, orgId]);

    const available = useMemo(() => counterparties.filter(c => {
        if (c.archivedAt && c.id !== counterpartyId) return false;  // архив — только у старой операции, где он уже выбран
        if (mode === 'PAYOUT') return c.type === CounterpartyType.INDIVIDUAL;
        if (mode === 'DIVIDENDS') return c.type === CounterpartyType.STAFF || c.type === CounterpartyType.INDIVIDUAL;
        return c.type === CounterpartyType.LEGAL_ENTITY || c.type === CounterpartyType.ENTREPRENEUR;
    }), [counterparties, mode, counterpartyId]);

    const total = amountOnly || volumeMode ? Number(amount) || 0 : items.reduce((s, i) => s + i.amount, 0);
    const bonusAmount = isSelfEmployedPayout && payoutLabel === 'SALARY' && bonusSplit
        ? Math.round((parseFloat(bonusText.replace(/\s/g, '').replace(',', '.')) || 0) * 100) / 100 : null;
    const bonusProblem = bonusAmount === null ? null : bonusAmount <= 0 ? 'Укажите, сколько из выплаты — премия'
        : bonusAmount >= total ? 'Премия не меньше суммы выплаты — выберите «Премия»' : null;
    const vatIncluded = vatRate > 0 ? total * vatRate / (100 + vatRate) : 0;

    // «Оплата» контрагенту → черновик платёжки в Т-Банке (ИП подключён по API, оплата с расчётного счёта в Т-Банке)
    const tbank = useTbankOrgs();
    const [toBank, setToBank] = useState(false);
    const [purpose, setPurpose] = useState('');
    const [purposeTouched, setPurposeTouched] = useState(false);
    useEffect(() => { setToBank(false); setPurposeTouched(false); }, [mode, editing]);
    const payCp = counterparties.find(c => c.id === counterpartyId);
    const bankShown = mode === 'PAYMENT' && !editing && tbank.includes(orgId);
    const tbankAccount = method === 'bank' && org?.accounts.find(a => a.id === accountId)?.bik === TBANK_BIK;
    const cpMissing = payCp ? [!payCp.accountNumber && 'расчётный счёт', !payCp.bik && 'БИК'].filter(Boolean) as string[] : [];
    const canDraft = bankShown && tbankAccount && !!payCp && cpMissing.length === 0;
    const rub = (n: number) => n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const autoPurpose = `${items.map(i => i.name).filter(Boolean).join(', ') || category || 'Оплата'}. ${vatRate > 0 ? `В т.ч. НДС ${vatRate}% — ${rub(vatIncluded)} руб.` : 'Без НДС'}`;
    useEffect(() => { if (!purposeTouched) setPurpose(autoPurpose.slice(0, 210)); }, [autoPurpose, purposeTouched]);

    // НДФЛ с дивидендов (ООО — налоговый агент) — тем же движком на сервере
    useEffect(() => {
        if (mode !== 'DIVIDENDS' || !org || !counterpartyId || !(Number(amount) > 0)) { setDivNdfl(null); return; }
        const timer = setTimeout(() => api.payrollPreview({ organizationId: org.id, date, kind: 'DIVIDENDS',
                items: [{ counterpartyId, amount: Number(amount) }] })
            .then(p => setDivNdfl(p.items[0]?.ndfl ?? null)).catch(() => setDivNdfl(null)), 300);
        return () => clearTimeout(timer);
    }, [mode, org, counterpartyId, amount, date]);

    if (!mode) return null;

    const changeDate = (d: string) => {
        setDate(d);
        if (isIncome && !editing && !informal && d) setVatRate(incomeVatRateFor(org, d));
    };
    const updateItem = (id: string, field: keyof TransactionItem, value: any) => setItems(prev => prev.map(i => {
        if (i.id !== id) return i;
        const u = { ...i, [field]: value };
        if (field === 'quantity' || field === 'price') u.amount = Number(u.quantity) * Number(u.price);
        return u;
    }));

    // Соседний год для стыка: январь–февраль → прошлый, декабрь → следующий
    const txYear = Number(date.slice(0, 4)), txMonth = Number(date.slice(5, 7));
    const seamYear = txMonth <= 2 ? txYear - 1 : txMonth === 12 ? txYear + 1 : (planningYear ?? null);

    const submitVolumes = async () => {
        if (bonusProblem) { showNotification(bonusProblem, 'error'); return; }
        const problem = volumeProblems(volumeLines, total);
        if (problem) { showNotification(problem, 'error'); return; }
        setBusy(true);
        try {
            const r = await api.volumePayout({ counterpartyId, projectId, organizationId: orgId, date, amount: total,
                lines: volumeLines!.filter(l => l.qty > 0).map(l => ({ code: l.code, qty: l.qty, price: l.price })),
                paymentMethod: method, bankAccountId: method === 'bank' ? accountId || null : null, hasDocument, payoutLabel, sendToCrm,
                bonusAmount });
            rememberOrg();
            refreshData();
            showNotification(`Выплата проведена: задание №${r.taskNumber}, акт №${r.actNumber}. CRM: ${r.crmStatus === 'SENT' ? 'передано' : r.crmStatus === 'SKIPPED' ? 'не передавалось' : r.crmError || 'не передано'}`,
                             r.crmStatus === 'SENT' || r.crmStatus === 'SKIPPED' ? 'success' : 'error');
            onClose();
        } catch (err: any) { showNotification(err?.message || 'Ошибка', 'error'); }
        setBusy(false);
    };

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!orgId) { showNotification('Выберите организацию', 'error'); return; }
        const needCp = (mode === 'INCOME' && !informal) || mode === 'PAYMENT' || mode === 'PAYOUT' || mode === 'DIVIDENDS';
        if (needCp && !counterpartyId) { showNotification('Выберите контрагента', 'error'); return; }
        if (total <= 0) { showNotification('Сумма должна быть больше нуля', 'error'); return; }
        if (needsBasis && !incomeBasis) { showNotification('Приход «не доход»: выберите основание', 'error'); return; }
        if (isSelfEmployedPayout && bonusProblem) { showNotification(bonusProblem, 'error'); return; }
        if (volumeMode) { submitVolumes(); return; }
        const txKind: TransactionKind = mode === 'TAX' ? 'TAX' : mode === 'WITHDRAWAL' ? 'OWNER_WITHDRAWAL'
            : mode === 'DIVIDENDS' ? 'DIVIDENDS' : informal ? 'NON_TAX' : kind;
        // Правка импортированной операции: назначение из выписки не затираем, если категорию не меняли
        const keepText = editing?.description && category === editing.category;
        const description = amountOnly ? (keepText ? editing!.description : category || MODE_TITLES[mode]) : (items.map(i => i.name).filter(Boolean).join(', ') || category);
        const data = {
            type: isIncome ? TransactionType.INCOME : TransactionType.EXPENSE,
            date, amount: total, counterpartyId: needCp || (informal && counterpartyId) ? counterpartyId : undefined, category, description,
            status: isIncome && !informal ? status : TransactionStatus.PAID, kind: txKind,
            hasVat: !notIncome && vatRate > 0, vatRate: notIncome ? 0 : vatRate, items: amountOnly ? [] : items,
            incomeBasis: notIncome ? incomeBasis || null : null,
            planningYear: seamYear && planningYear === seamYear ? planningYear : null,
            paymentMethod: method, bankAccountId: method === 'bank' ? accountId : null,
            hasDocument: amountOnly ? true : hasDocument,
            taxAccount: mode === 'TAX' ? taxAccount : null,
            projectId: projectId || null,
            // Самозанятому: зарплата или премия — для учёта и статьи в планировании
            payoutLabel: isSelfEmployedPayout ? payoutLabel : mode === 'WITHDRAWAL' ? (wdSubtype || null) : undefined,
            ...(isSelfEmployedPayout ? { bonusAmount } : {}),
            // Выплата самозанятому — сама уходит в «Зарплату» lan-install; галочка «Не передавать» (backend crm.auto_push)
            ...((isSelfEmployedPayout || (mode === 'WITHDRAWAL' && wdSubtype)) && !editing ? { sendToCrm } : {}),
        };
        rememberOrg();
        if (editing) updateTransaction(editing.id, reviewed ? { ...data, reviewNote: null } : data);
        else {
            const saved = addTransaction({ ...data, organizationId: orgId, status: isIncome && !informal ? TransactionStatus.PENDING : TransactionStatus.PAID });
            if (canDraft && toBank) {
                const text = purpose.trim() || autoPurpose;
                saved.then(id => id ? api.createPaymentDraft(id, text.slice(0, 210)) : null)
                    .then(r => r && showNotification(`Черновик №${r.number} в Т-Банке: ${r.recipient} — подпишите в Т-Бизнесе`, 'success'))
                    .catch(e => showNotification(e?.message || 'Черновик в Т-Банке не создан', 'error'));
            }
        }
        onClose();
    };

    return (
        <Modal isOpen={!!mode} onClose={onClose} title={editing ? 'Операция' : mode === 'PAYOUT' && onSwitchPayKind ? 'Зарплата' : MODE_TITLES[mode]}>
            <form onSubmit={submit} className="space-y-3">
                {mode === 'PAYOUT' && !editing && onSwitchPayKind && (
                    <PayKindTabs value="SELF_EMPLOYED" onChange={k => { if (k !== 'SELF_EMPLOYED') onSwitchPayKind(k); }} />
                )}
                <OrgSelect orgs={orgs} value={orgId} onChange={setOrgId} locked={!!fixedOrgId || !!editing} />
                {org?.closedUntil && (editing ? editing.date : date) <= org.closedUntil && (
                    <p className="text-[11px] text-amber-800 bg-amber-50 p-2 rounded">Период по {formatDay(org.closedUntil)} закрыт (переход на приложение):
                        операции до этой даты не создаются и не меняются — можно только снять пометку «проверить».</p>
                )}
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Дата" type="date" value={date} onChange={e => changeDate(e.target.value)} required />
                    {(mode === 'INCOME' || mode === 'PAYMENT' || mode === 'PAYOUT' || mode === 'DIVIDENDS') && (
                        <Select label={isIncome ? (informal ? 'От кого (необязательно)' : 'Контрагент') : 'Получатель'} value={counterpartyId} onChange={e => setCounterpartyId(e.target.value)}
                            options={[{ value: '', label: informal ? 'Не указан' : 'Выберите...' }, ...(informal ? counterparties : available).map(c => ({ value: c.id, label: c.name }))]} />
                    )}
                    {mode === 'TAX' && (
                        <Select label="Куда" value={taxAccount} onChange={e => setTaxAccount(e.target.value as TaxAccount)} options={[
                            { value: 'ENS', label: 'Пополнение ЕНС (налоги и взносы)' }, { value: 'SFR', label: 'СФР (травматизм)' }]} />
                    )}
                </div>
                {mode === 'PAYOUT' && !editing && <CrmDebtHint balances={crmBalances} counterpartyId={counterpartyId} />}
                {seamYear && (
                    <Select label="Планирование" value={planningYear === seamYear ? String(seamYear) : ''}
                        onChange={e => setPlanningYear(e.target.value ? Number(e.target.value) : null)}
                        options={[{ value: '', label: `${txYear} год (по дате операции)` },
                            { value: String(seamYear), label: seamYear < txYear ? `За ${seamYear} год — в планирование ${seamYear}` : `В планирование ${seamYear} года` }]} />
                )}
                <Input label={informal ? 'За что' : amountOnly ? 'Назначение' : 'Категория'} value={category} onChange={e => setCategory(e.target.value)} required
                    list={informal ? 'informal-income' : undefined} />
                {informal && <datalist id="informal-income">
                    {['Сдача лома / обрезков кабеля', 'Заём', 'Взнос собственника', 'Возврат денег', 'Прочий приход'].map(v => <option key={v} value={v} />)}
                </datalist>}
                {isSelfEmployedPayout && (
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-slate-500">Для учёта:</span>
                        {([['SALARY', 'Зарплата', false], ['BONUS', 'Премия', false], ['SALARY', 'ЗП + премия', true]] as const).map(([v, l, split]) => (
                            <label key={l} className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" name="payoutLabelSE" checked={payoutLabel === v && bonusSplit === split}
                                    onChange={() => { setPayoutLabel(v); setBonusSplit(split);
                                                      if (!editing && ['Выплата', 'Зарплата', 'Премия'].includes(category)) setCategory(split ? 'Зарплата' : l); }} /> {l}
                            </label>
                        ))}
                        {bonusSplit && <>
                            <span className="flex items-center gap-1">из них премия
                                <input className="w-28 rounded border border-slate-300 px-2 py-1 text-right" inputMode="decimal" value={bonusText}
                                       onChange={e => setBonusText(e.target.value)} placeholder="0" /> ₽</span>
                            <span className="text-slate-500">ЗП {money(Math.max(0, total - (bonusAmount || 0)))} ₽ · только для учёта и планирования, в документах не видно</span>
                        </>}
                    </div>
                )}
                {(mode === 'INCOME' || mode === 'PAYMENT' || mode === 'PURCHASE' || mode === 'PAYOUT') && (
                    <ProjectSelect projects={projects} value={projectId} onChange={setProjectId} alsoClosed={poolProjects} />
                )}

                {((mode === 'INCOME' && !informal) || mode === 'PAYMENT' || mode === 'PURCHASE') && (
                    <Select value={kind} onChange={e => setKind(e.target.value as 'REGULAR' | 'NON_TAX')} options={isIncome
                        ? [{ value: 'REGULAR', label: 'Выручка (облагается налогом)' }, { value: 'NON_TAX', label: 'Не доход (основание ниже)' }]
                        : [{ value: 'REGULAR', label: 'Расход' }, { value: 'NON_TAX', label: 'Не расход: возврат займа, перевод' }]} />
                )}

                {isIncome && notIncome && (
                    <Select label={`Основание «не дохода»${needsBasis ? ' (обязательно)' : ''}`} value={incomeBasis}
                        onChange={e => setIncomeBasis(e.target.value)} options={[{ value: '', label: 'Выберите…' },
                            ...Object.entries(INCOME_BASES).map(([value, label]) => ({ value, label }))]} />
                )}
                {isSelfEmployedPayout && !editing && !volumeMode && (
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
                        <input type="checkbox" checked={!sendToCrm} onChange={e => setSendToCrm(!e.target.checked)} />
                        Не передавать в lan-install (выплата там уже учтена)
                    </label>
                )}
                {volumeMode ? (
                    <>
                        <Input label="Сумма выплаты" type="number" min={0} step="0.01" value={amount}
                            onChange={e => setAmount(e.target.value)} required />
                        <VolumePick projectId={projectId} orgId={orgId} target={total} lines={volumeLines} onChange={setVolumeLines} />
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
                            <input type="checkbox" checked={!sendToCrm} onChange={e => setSendToCrm(!e.target.checked)} />
                            Не передавать в lan-install (выплата там уже учтена)
                        </label>
                    </>
                ) : amountOnly ? (
                    <Input label={mode === 'DIVIDENDS' ? 'Сумма на руки' : 'Сумма'} type="number" min={0} step="0.01"
                        value={amount} onChange={e => setAmount(e.target.value)} required />
                ) : (
                    <div className="border rounded-lg overflow-hidden border-slate-300">
                        <div className="bg-slate-100 p-1.5 border-b border-slate-200 font-semibold text-[10px] text-slate-600">Список товаров / услуг</div>
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 text-slate-500"><tr><th className="p-1.5 text-left">Наименование</th><th className="p-1.5 w-14">Кол-во</th><th className="p-1.5 w-12">Ед.</th><th className="p-1.5 w-20">Цена</th><th className="p-1.5 w-20 text-right">Сумма</th><th className="w-6"></th></tr></thead>
                            <tbody>{items.map(item => (
                                <tr key={item.id} className="border-t border-slate-100">
                                    <td className="p-1"><input className="w-full p-1 border border-slate-300 rounded" value={item.name} onChange={e => updateItem(item.id, 'name', e.target.value)} placeholder="Название..." /></td>
                                    <td className="p-1"><input className="w-full p-1 border border-slate-300 rounded text-center" type="number" value={item.quantity || ''} onChange={e => updateItem(item.id, 'quantity', Number(e.target.value))} /></td>
                                    <td className="p-1"><input className="w-full p-1 border border-slate-300 rounded text-center" value={item.unit} onChange={e => updateItem(item.id, 'unit', e.target.value)} /></td>
                                    <td className="p-1"><input className="w-full p-1 border border-slate-300 rounded text-right" type="number" value={item.price || ''} onChange={e => updateItem(item.id, 'price', Number(e.target.value))} placeholder="0" /></td>
                                    <td className="p-1.5 text-right">{formatMoney(item.amount)}</td>
                                    <td className="text-center">{items.length > 1 && <button type="button" onClick={() => setItems(items.filter(i => i.id !== item.id))} className="text-slate-400 hover:text-red-600"><Trash2 size={14}/></button>}</td>
                                </tr>))}
                            </tbody>
                        </table>
                        <div className="p-2 bg-slate-50 border-t border-slate-200 flex justify-between items-start">
                            <button type="button" onClick={() => setItems([...items, newItem()])} className="text-[10px] text-blue-600 font-bold flex items-center gap-1 uppercase"><Plus size={12}/> Добавить</button>
                            <div className="text-right">
                                <div><span className="text-xs text-slate-500">Итого: </span><span className="text-base font-bold">{formatMoney(total)}</span></div>
                                {vatRate > 0 && <div className="text-[11px] text-slate-500">в т.ч. НДС {vatRate}%: {formatMoney(vatIncluded)}</div>}
                            </div>
                        </div>
                    </div>
                )}

                {/* НДС: в выручке — ставка по режиму организации; входящий — только при общей ставке с вычетами */}
                {((isIncome && !informal) || ((mode === 'PAYMENT' || mode === 'PURCHASE') && vatGeneral)) && (
                    <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-slate-700">{isIncome ? 'НДС:' : 'Входящий НДС:'}</label>
                        <div className="w-32"><Select value={String(vatRate)} onChange={e => setVatRate(Number(e.target.value))} options={VAT_RATES} /></div>
                    </div>
                )}

                {(!isIncome || informal) && (
                    <div className="bg-slate-50 p-2 rounded border border-slate-200 space-y-2">
                        <Select label={informal ? 'Куда поступили' : 'Способ оплаты'} value={method === 'cash' ? 'cash' : accountId}
                            onChange={e => { if (e.target.value === 'cash') setMethod('cash'); else { setMethod('bank'); setAccountId(e.target.value); } }}
                            options={[...(org?.accounts || []).map(a => ({ value: a.id, label: accountLabel(a) })), { value: 'cash', label: 'Наличные' }]} />
                        {!amountOnly && (
                            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
                                <input type="checkbox" checked={hasDocument} onChange={e => setHasDocument(e.target.checked)} />
                                {isSelfEmployedPayout ? 'Есть чек из «Мой налог»' : 'Есть подтверждающий документ (акт, чек)'}
                            </label>
                        )}
                        {!amountOnly && !hasDocument && isSelfEmployedPayout && (
                            <p className="text-[11px] text-amber-700 flex items-center gap-1"><AlertTriangle size={12}/> Без чека самозанятого выплата попадёт в контроль недостающих документов.</p>
                        )}
                        {!amountOnly && !hasDocument && !isSelfEmployedPayout && !isUSN6 && kind === 'REGULAR' && (
                            <p className="text-[11px] text-slate-500">Расход без документа уменьшает баланс, но не налоговую базу.</p>
                        )}
                    </div>
                )}

                {informal && <p className="text-[11px] text-slate-500">Только на баланс: без налогов, НДС и документов. Официальную выручку проводите через «Реализацию».</p>}
                {mode === 'TAX' && <p className="text-[11px] text-slate-500">Пополнение ЕНС уменьшает банковский баланс и пополняет сальдо ЕНС; налоги из него гасятся зачётом в календаре.</p>}
                {mode === 'WITHDRAWAL' && (
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-slate-500">Подвид:</span>
                        {([['', 'Бонус', 'статья партнёра в планировании'], ['SALARY', 'ЗП партнёра', 'статья «ЗП», как в таблице'],
                           ['BONUS', 'Премия партнёра', 'статья «Премия»']] as const).map(([v, l, hint]) => (
                            <label key={v} className="flex items-center gap-1 cursor-pointer" title={hint}>
                                <input type="radio" name="wdSubtype" checked={wdSubtype === v} onChange={() => setWdSubtype(v)} /> {l}
                            </label>
                        ))}
                    </div>
                )}
                {mode === 'WITHDRAWAL' && wdSubtype && !editing && (
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
                        <input type="checkbox" checked={!sendToCrm} onChange={e => setSendToCrm(!e.target.checked)} />
                        Не передавать в lan-install (выплата там уже учтена)
                    </label>
                )}
                {mode === 'WITHDRAWAL' && <p className="text-[11px] text-slate-500">Вывод себе уменьшает баланс, но не является расходом: не влияет на налоги и прибыль.</p>}
                {mode === 'DIVIDENDS' && divNdfl !== null && (
                    <p className="text-[11px] text-purple-800 bg-purple-50 p-2 rounded">НДФЛ с дивидендов: {formatMoney(divNdfl)} — удержать и перечислить (обязательство появится в календаре). Срок — по дате выплаты {formatDay(date)}.</p>
                )}

                {isIncome && editing && (
                    <div className="w-48"><Select value={status} onChange={e => setStatus(e.target.value as TransactionStatus)} options={[
                        { value: TransactionStatus.PENDING, label: 'Ожидает оплаты' }, { value: TransactionStatus.PAID, label: 'Оплачено' }]} /></div>
                )}

                {bankShown && (
                    <div className="p-2 rounded-lg border border-slate-200 text-xs space-y-1">
                        <label className={`flex items-center gap-2 ${canDraft ? 'cursor-pointer' : 'text-slate-400'}`}>
                            <input type="checkbox" checked={canDraft && toBank} disabled={!canDraft} onChange={e => setToBank(e.target.checked)} />
                            <span className="font-medium">Создать черновик платёжки в Т-Банке</span>
                        </label>
                        {!tbankAccount ? <p className="text-[11px] text-slate-500 ml-5">Выберите способ оплаты — расчётный счёт в Т-Банке.</p>
                         : !payCp ? <p className="text-[11px] text-slate-500 ml-5">Выберите получателя.</p>
                         : cpMissing.length ? <p className="text-[11px] text-amber-700 ml-5">В карточке «{payCp.name}» нет: {cpMissing.join(', ')} — заполните в «Справочниках».</p>
                         : toBank && <>
                            <p className="text-[11px] text-slate-500 ml-5">{payCp.name}{payCp.inn ? `, ИНН ${payCp.inn}` : ''} · {payCp.bankName || 'счёт'} ···{payCp.accountNumber!.slice(-4)}, БИК {payCp.bik}.
                                Черновик появится в Т-Бизнесе в «Платежах» — деньги уйдут после вашей подписи.</p>
                            <label className="block ml-5 text-[11px] text-slate-600">Назначение платежа
                                <textarea value={purpose} maxLength={210} rows={2} onChange={e => { setPurpose(e.target.value); setPurposeTouched(true); }}
                                    className="block w-full mt-0.5 border border-slate-300 rounded px-2 py-1 text-xs" /></label>
                        </>}
                    </div>
                )}
                <ReviewCheckbox note={editing?.reviewNote} checked={reviewed} onChange={setReviewed} />
                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                    <Button type="button" variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                    <Button type="submit" variant={editing ? 'primary' : isIncome ? 'success' : 'danger'} size="sm" disabled={busy}>
                        {editing ? 'Сохранить' : isIncome ? 'Выставить' : 'Провести'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
